import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";

import { createContext, memo, useMemo } from "react";
import { ScheduleDemoProvider } from "../conversion/common/schedule-demo-provider";

/**
 * Describes how much of the Conversion Cloud a team can use. There is exactly
 * one signal for "is intent on?": `predictiveConversion.enabled`. Combined with
 * deal size (annualOrders × 30¢ vs $3,000) that yields five states:
 *
 * Intent OFF (predictiveConversion.enabled = false), no secondary nav:
 * Both land on the intent-rescue welcome page — the self-serve upsell pitch to
 * turn intent on. The deal-size split only affects navigation, not the landing:
 * - "intent-rescue-small": below the deal-size threshold.
 * - "none": above the threshold. (The generic Conversion welcome page is no
 *   longer shown to self-serve teams, but DefaultWelcomePage is kept around as
 *   a fallback / revert path.)
 *
 * Intent ON (predictiveConversion.enabled = true), secondary nav present:
 * - "intent-only-small": below the deal-size threshold. Trimmed sidebar
 *   (Dashboard + Spotlights) and the small intent dashboard. Too small to be
 *   worth a sales call, so no locked upsell items.
 * - "intent-only": above the threshold. Full sidebar with the non-intent
 *   features locked (lock icon → schedule-a-call); Dashboard and Spotlights
 *   stay open.
 *
 * - "full": admin has flipped the Conversion (checkout optimization) toggle on;
 *   everything unlocked.
 */
export type ConversionAccessLevel =
  | "none"
  | "intent-rescue-small"
  | "intent-only-small"
  | "intent-only"
  | "full";

/**
 * Coverage price assumed for the deal-size heuristic (per CRO-1430). The
 * ticket specifies a flat 30¢ per order, independent of the team's actual
 * configured coverage price, so this is intentionally a constant.
 */
export const COVERAGE_PRICE_CENTS = 30;

/**
 * Estimated annual revenue (in cents) below which a self-serve Intent team is
 * considered too small to justify a sales call. annualOrders × 30¢ < $3,000.
 */
export const INTENT_ONLY_SMALL_DEAL_SIZE_CENTS = 300_000;

/**
 * Pure derivation of access level from the underlying team flags + order
 * volume. Extracted so it can be unit-tested without mounting React.
 *
 * "full" (checkout optimization) wins over everything — a team onboarded by
 * sales should never see the intent/locked UI.
 *
 * Otherwise the team splits on a single intent signal
 * (`predictiveConversionEnabled`) and then on deal size:
 *   intent on  → small ? "intent-only-small"   : "intent-only"
 *   intent off → small ? "intent-rescue-small" : "none"
 *
 * `annualOrders === null` (still loading or RPC failed) counts as below the
 * threshold (0 orders), i.e. the "small" branch — we'd rather briefly treat a
 * borderline team as small than flash the wrong page at a clearly-small one.
 */
export function deriveConversionAccessLevel(input: {
  checkoutOptimizationEnabled: boolean;
  predictiveConversionEnabled: boolean;
  annualOrders: number | null;
}): ConversionAccessLevel {
  if (input.checkoutOptimizationEnabled) {
    return "full";
  }
  const estimatedDealSizeCents =
    (input.annualOrders ?? 0) * COVERAGE_PRICE_CENTS;
  const isSmall = estimatedDealSizeCents < INTENT_ONLY_SMALL_DEAL_SIZE_CENTS;
  if (input.predictiveConversionEnabled) {
    return isSmall ? "intent-only-small" : "intent-only";
  }
  return isSmall ? "intent-rescue-small" : "none";
}

export interface ConversionSuiteStatuses {
  conversionEnabled: boolean;
  checkoutOptimizationEnabled: boolean;
  checkoutOutboundRatesEnabled: boolean;
  accessLevel: ConversionAccessLevel;
  /**
   * False while the order-count RPC that distinguishes "intent-only" from
   * "intent-only-small" is still in flight. During that window `accessLevel`
   * optimistically reads "intent-only-small" (see deriveConversionAccessLevel),
   * which is fine for the sidebar but NOT for surfaces that fire
   * page-view analytics — a large intent team would briefly mount the
   * intent-rescue upsell page and emit a spurious UPSELL_PAGE_VIEWED before
   * the RPC resolves them to "intent-only". Such surfaces should wait for
   * this to be true before committing to a tier.
   */
  accessLevelResolved: boolean;
}

export const ConversionSuiteStatusesContext = createContext<
  ConversionSuiteStatuses | undefined
>(undefined);

export const ConversionSuiteStatusesProvider = memo(
  function ConversionSuiteStatusesProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const team = useRequiredContext(TeamContext);
    const conversionRpcClient = useRequiredContext(
      RedoConversionRpcClientContext,
    );
    const settings = team.settings;

    const checkoutOptimizationEnabled =
      !!settings.checkoutOptimization?.enabled;
    const checkoutOutboundRatesEnabled =
      !!team.shopifyIntegration?.checkoutOutboundRatesEnabled;
    const conversionEnabled =
      checkoutOptimizationEnabled && checkoutOutboundRatesEnabled;
    const predictiveConversionEnabled =
      !!settings.predictiveConversion?.enabled;

    // Fetch order counts for every non-"full" team. Deal size splits the small
    // tiers from the large ones on both the intent-on and intent-off sides
    // (intent-only-small vs intent-only, intent-rescue-small vs none), so the
    // count is needed whenever checkout optimization is off.
    const shouldFetchOrderCounts = !checkoutOptimizationEnabled;

    const orderCountsLoad = useLoad(async () => {
      if (!shouldFetchOrderCounts) {
        return null;
      }
      return await conversionRpcClient.getOnlineStoreOrderCounts();
    }, [conversionRpcClient, shouldFetchOrderCounts]);

    const annualOrders: number | null =
      orderCountsLoad.value && orderCountsLoad.value.success
        ? orderCountsLoad.value.lastYearCount
        : null;

    const accessLevel = deriveConversionAccessLevel({
      checkoutOptimizationEnabled,
      predictiveConversionEnabled,
      annualOrders,
    });

    // Resolved when we didn't need the RPC at all (none/full decided purely
    // from team flags) or the RPC has settled with a value or an error.
    const accessLevelResolved =
      !shouldFetchOrderCounts ||
      orderCountsLoad.value !== undefined ||
      orderCountsLoad.error !== undefined;

    const conversionSuiteStatuses = useMemo(
      () => ({
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
        accessLevelResolved,
      }),
      [
        conversionEnabled,
        checkoutOptimizationEnabled,
        checkoutOutboundRatesEnabled,
        accessLevel,
        accessLevelResolved,
      ],
    );

    return (
      <ConversionSuiteStatusesContext.Provider value={conversionSuiteStatuses}>
        <ScheduleDemoProvider>{children}</ScheduleDemoProvider>
      </ConversionSuiteStatusesContext.Provider>
    );
  },
);

export { default as POST_PURCHASE_UPSELL_ICON } from "@redotech/redo-icons/cursor-click-01.svg";
export { default as POST_PURCHASE_UPSELL_ICON_FILLED } from "@redotech/redo-icons/cursor-click-01_filled.svg";
export { default as CONVERSION_ICON } from "@redotech/redo-icons/shopping-cart-03.svg";
export { default as CONVERSION_ICON_FILLED } from "@redotech/redo-icons/shopping-cart-03_filled.svg";
